/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React from 'react';
import PropTypes from 'prop-types';
import { Check, Shield, User, Key, FileText, Settings, Globe, Layers, Camera, Webhook, Bot, Volume2, Palette, FileJson, PhoneCall } from 'lucide-react';

const ENTITY_CONFIG = {
  accounts: { label: 'Account Management', icon: Globe, color: 'text-blue-500' },
  sub_accounts: { label: 'Sub Accounts', icon: Globe, color: 'text-blue-300' },
  users: { label: 'User Management', icon: User, color: 'text-purple-500' },
  roles: { label: 'Role & Permissions', icon: Shield, color: 'text-orange-500' },
  permissions: { label: 'Permissions', icon: Key, color: 'text-yellow-500' },
  assignments: { label: 'Assignment Access', icon: FileText, color: 'text-green-500' },
  templates: { label: 'Workflow Builder', icon: Layers, color: 'text-pink-500' },
  media: { label: 'Media Access', icon: Camera, color: 'text-indigo-500' },
  webhooks: { label: 'Webhook Settings', icon: Webhook, color: 'text-cyan-500' },
  settings: { label: 'Other Settings', icon: Settings, color: 'text-gray-500' },
  audio: { label: 'Audio Access', icon: Volume2, color: 'text-rose-500' },
  ai: { label: 'AI Assistant', icon: Bot, color: 'text-violet-500' },
  themes: { label: 'Theme Builder', icon: Palette, color: 'text-fuchsia-500' },
  bulk: { label: 'Bulk Operations', icon: FileJson, color: 'text-emerald-500' },
  agents: { label: 'Agent Tools', icon: PhoneCall, color: 'text-amber-500' },
};

const PermissionGrid = ({ permissions, selectedIds, onChange, isLoading }) => {
  // Group permissions by the prefix before the first dot (e.g., "assignments.view" → "assignments")
  const grouped = permissions.reduce((acc, p) => {
    const dotIndex = p.key.indexOf('.');
    const groupName = dotIndex !== -1 ? p.key.slice(0, dotIndex).toLowerCase() : 'settings';

    if (!acc[groupName]) acc[groupName] = [];
    acc[groupName].push(p);
    return acc;
  }, {});

  const togglePermission = (id) => {
    const perm = permissions.find(p => p.id === id);
    if (!perm) return;

    const isSelecting = !selectedIds.includes(id);
    let newSelected;

    if (isSelecting) {
      newSelected = [...selectedIds, id];

      // If selecting a 'manage' action, auto-select all in the same group
      const action = perm.key.split('.')[1]?.toLowerCase();
      if (action === 'manage') {
        const groupName = perm.key.split('.')[0]?.toLowerCase();
        if (groupName && grouped[groupName]) {
          const groupIds = grouped[groupName].map(p => p.id);
          newSelected = Array.from(new Set([...newSelected, ...groupIds]));
        }
      }
    } else {
      newSelected = selectedIds.filter(sid => sid !== id);

      // If unselecting any permission, unselect the 'manage' permission of that group
      const groupName = perm.key.split('.')[0]?.toLowerCase();
      if (groupName && grouped[groupName]) {
        const managePerm = grouped[groupName].find(p =>
          p.key.split('.')[1]?.toLowerCase() === 'manage'
        );
        if (managePerm && id !== managePerm.id) {
          newSelected = newSelected.filter(sid => sid !== managePerm.id);
        }
      }
    }

    onChange(newSelected);
  };

  const getActionLabel = (key) => {
    // Extract the action part after the first dot
    const parts = key.split('.');
    if (parts.length < 2) { return key; }

    const action = parts.slice(1).join('_'); // handles keys like "assignments.view_media"
    return action
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <div className="space-y-6">
      <style>{`
        input[type='checkbox']:hover { background-color: inherit; }
        input[type='checkbox']:checked:hover { background-color: var(--color-primary, #3b82f6); }
      `}</style>
      {/* Header */}
      <div className="space-y-4">
        <h2 className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em]">
          Assign Permissions
        </h2>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-base-200/50 rounded-3xl border border-dashed border-base-300">
          <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin mb-4" />
          <p className="text-[10px] font-black text-base-content/40 uppercase tracking-widest">
            Loading Permission Matrix...
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {Object.entries(grouped).map(([entity, groupPermissions]) => {
            const config = ENTITY_CONFIG[entity] || {
              label: entity.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
              icon: Shield,
              color: 'text-base-content/40',
            };

            return (
              <div
                key={entity}
                className="border border-base-300 rounded-2xl shadow-sm overflow-hidden flex flex-col"
              >
                <div className="px-3 py-3 border-b border-base-200 bg-base-200">
                  <h3 className="text-sm font-black text-base-content tracking-tight">
                    {config.label}
                  </h3>
                </div>

                <div className="px-3 py-3 grid grid-cols-2 gap-x-4 gap-y-3">
                  {groupPermissions.map(p => {
                    const isSelected = selectedIds.includes(p.id);
                    return (
                      <label
                        key={p.id}
                        className="flex items-center gap-2 cursor-pointer group select-none"
                      >
                        <div className="relative flex items-center justify-center flex-shrink-0">
                          <input
                            type="checkbox"
                            className="peer appearance-none w-5 h-5 rounded-md border border-base-300 bg-base-100 checked:bg-primary checked:border-primary hover:border-base-400 checked:hover:bg-primary transition-all cursor-pointer"
                            checked={isSelected}
                            onChange={() => togglePermission(p.id)}
                          />
                          <Check
                            size={12}
                            className="absolute text-primary-content opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none"
                          />
                        </div>
                        <span
                          className={`text-sm font-medium transition-colors leading-tight ${isSelected ? 'text-base-content' : 'text-base-content/60 group-hover:text-base-content'
                            }`}
                        >
                          {getActionLabel(p.key)}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PermissionGrid;

PermissionGrid.propTypes = {
  permissions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      key: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  onChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};